import React from "react";
import { KeyRound, Lock, ServerCog, CreditCard, ClipboardList, ShieldCheck } from "lucide-react";

const groups = [
  { icon: KeyRound, title: "Identity Security", items: ["Multi-factor authentication", "Session JWT validation", "Tenant-scoped RBAC", "Isolated session storage"] },
  { icon: Lock, title: "Data Protection", items: ["Encrypted-at-rest reports", "Scoped export authorization", "Retention policy controls", "Per-tenant encryption keys"] },
  { icon: ServerCog, title: "Infrastructure Security", items: ["Cloudflare edge isolation", "Per-tenant queue partitioning", "Rate limit + abuse detection", "Secret rotation policies"] },
  { icon: CreditCard, title: "Billing Security", items: ["Stripe-hosted payments only", "Verified webhook signatures", "No card data ever stored", "Environment-scoped secrets"] },
  { icon: ClipboardList, title: "Operational Security", items: ["Append-only audit logs", "Anomaly + alert monitoring", "Quarterly access reviews", "Incident response runbooks"] },
  { icon: ShieldCheck, title: "Mandatory Ownership Verification", items: ["DNS-based control proof", "Verification expiry checks", "Replay-resistant tokens", "Tenant-bound mapping"] },
];

export default function Security() {
  return (
    <div>
      <section className="pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600 mb-5">Security</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02] ink">
            Defense in depth.<br /><span className="font-serif italic font-normal">By design.</span>
          </h1>
          <p className="mt-7 text-lg ink-soft leading-relaxed">
            TrustDmarc is engineered as a defense-in-depth platform — every layer enforces explicit, auditable trust boundaries.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map(({ icon: Icon, title, items }) => (
            <div key={title} className="panel p-7">
              <div className="h-11 w-11 rounded-lg bg-orange-50 flex items-center justify-center mb-5">
                <Icon className="h-5 w-5 text-orange-600" strokeWidth={2} />
              </div>
              <h3 className="font-display text-lg font-bold ink">{title}</h3>
              <ul className="mt-4 space-y-2">
                {items.map((i) => (
                  <li key={i} className="text-sm ink-soft flex items-start gap-1.5">
                    <span className="text-orange-600 mt-0.5 font-bold">›</span>{i}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="panel-shadow p-10">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600 mb-3">Principle</div>
                <h2 className="font-display text-3xl font-bold tracking-tight ink">Only verified domains.</h2>
                <p className="mt-4 ink-soft leading-relaxed">
                  Before any domain becomes active, administrative control is validated via DNS. Monitoring is disabled until verified. Ingestion routes only to verified tenants. Ownership changes trigger revalidation.
                </p>
              </div>
              <div className="bg-[#0F0E0C] rounded-lg p-5 font-mono text-xs text-stone-300 overflow-x-auto">
{`policy.verification {
  method:        "dns_txt"
  host:          "_trustdmarc.<domain>"
  token_entropy: 96-bit
  ttl_check:     900s
  revalidation:  on_dns_disappear
  audit_trail:   append-only
}`}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
